// Libs
import classNames from 'classnames/bind';
import { Box, Button, FormGroup, InputLabel, Stack, Typography } from '@mui/material';
import { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ChangeEvent, useContext, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { pdf } from '@react-pdf/renderer';
// Components, Layouts, Pages
import {
  BaseDatePicker,
  BaseFilters,
  BaseInputSearch,
  BaseSelect,
  BaseTable,
  BaseTableOption,
  BaseTableUser,
  BaseToolbar,
  ChangeValueModal,
  ConfirmModal,
  Loading,
} from '~/components/common';

import PageShell from '~/pages/common/pageShell/PageShell';
// Others
import useDebounce from '~/utils/hooks/useDebounce';
import {
  DateFormatEnum,
  RoleEnum,
  SearchByFilterContentEnum,
  StatusEnum,
  TimeFormatEnum,
} from '~/utils/enum/common';
import {
  BASE_PAGINATION_DEFAULT_CURRENT_PAGE,
  DEFAULT_DEBOUNCE_DELAY_TIME,
  DEFAULT_LIMIT_1000,
  DEFAULT_LIMIT_PAGE,
  DEFAULT_NUMBER_ZERO,
  DEFAULT_PAYROLL_FILE_NAME_PDF,
  EMPTY_STRING,
  FILTER_DEFAULT_VALUES,
} from '~/utils/constants/common';
import { StorageEnum } from '~/utils/enum/base';
import {
  convertMinuteToHours,
  formatDate,
  formatTimeUTC,
  formatTitleCase,
  getFullName,
} from '~/utils/helper/common';
import { useAppDispatch } from '~/utils/redux/hooks';
import { IQueryParamTimeClock, ITimeClock } from '~/utils/interface/timeClock';
import { IBaseOption, IFilterValueChange, IHeaderCellType, IQueryParamBase } from '~/utils/interface/common';
import { IPaginationResponse } from '~/utils/interface/base';
import { adminRouteAbsolute } from '~/utils/routes/admin';
import { staffRouteAbsolute } from '~/utils/routes/staff';
import { dataMonthExport } from '~/utils/mockData/timeClock';
import {
  deleteTimeClock,
  getTimeClockHoursWorked,
  getTimeClockList,
} from '~/thunks/timeClock/timeClockThunk';
import { selectIsRefreshTimeClockList, timeClockActions } from '~/thunks/timeClock/timeClockSlice';
import { getEmployeeList } from '~/thunks/employee/employeeThunk';
import { LoadingContext } from '~/context';
import { PdfPayroll } from '~/components/module/payroll';
import { IExportPayroll, IParamsHoursWorked } from '~/utils/interface/payroll';
// Styles, images, icons
import styles from './TimeClock.module.scss';
import { icons } from '~/assets';
import { schema } from './helper';

const cx = classNames.bind(styles);

const columns = (
  t: TFunction,
  onView: (id: string) => void,
  onDelete: (timeClock: ITimeClock) => void
): IHeaderCellType<ITimeClock>[] => {
  return [
    {
      id: 'account',
      label: t('table_head_title_resource_name'),
      minWidth: 120,
      render: ({ account: { avatarImage, firstName, lastName } }: ITimeClock) => {
        return <BaseTableUser user={{ avatar: avatarImage, firstName, lastName }} />;
      },
    },
    {
      id: 'job',
      label: t('table_head_title_job'),
      minWidth: 100,
      render: (record: ITimeClock) => {
        return record?.job?.name || EMPTY_STRING;
      },
    },
    {
      id: 'date',
      label: t('table_head_title_date'),
      minWidth: 100,
      render: (record: ITimeClock) => {
        return record?.date ? formatDate(record?.date, DateFormatEnum.DD_MMM_YYYY) : EMPTY_STRING;
      },
    },
    {
      id: 'startTime',
      label: t('table_head_title_check_in'),
      minWidth: 100,
      render: (record: ITimeClock) => {
        return record?.startTime
          ? formatTimeUTC(record?.startTime, TimeFormatEnum.HOUR_MINUTE_AM_PM)
          : EMPTY_STRING;
      },
    },
    {
      id: 'endTime',
      label: t('table_head_title_check_out'),
      minWidth: 100,
      render: (record: ITimeClock) => {
        return record?.endTime
          ? formatTimeUTC(record?.endTime, TimeFormatEnum.HOUR_MINUTE_AM_PM)
          : EMPTY_STRING;
      },
    },
    {
      id: 'duration',
      minWidth: 50,
      label: t('table_head_title_total_time'),
      render: (record: ITimeClock) =>
        record?.duration ? convertMinuteToHours(Number(record?.duration)) : EMPTY_STRING,
    },
    {
      id: 'location',
      label: t('table_head_title_location'),
      minWidth: 100,
      render: (record: ITimeClock) => record?.location || EMPTY_STRING,
    },
    {
      id: 'remark',
      label: t('table_head_title_remark'),
      align: 'center',
      render: (record: ITimeClock) => (record?.status ? formatTitleCase(record?.status) : EMPTY_STRING),
    },
    {
      id: 'action',
      label: t('table_head_title_action'),
      align: 'left',
      render: (record: ITimeClock) => {
        return (
          <BaseTableOption
            onView={() => onView(record.id)}
            onDelete={() => onDelete(record)}
          />
        );
      },
    },
  ];
};

const TimeClock = () => {
  //#region Destructuring Props
  const { t } = useTranslation(['admin/timeClock']);
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const [searchParams, setSearchParams] = useSearchParams();
  const params = useMemo(() => Object.fromEntries([...searchParams]), [searchParams]);
  const pageSelected = useMemo<number>(
    () => Number(params?.page ?? BASE_PAGINATION_DEFAULT_CURRENT_PAGE),
    [params?.page]
  );
  const limitPage = useMemo<number>(() => Number(params?.limit ?? DEFAULT_LIMIT_PAGE), [params?.limit]);
  const textSearchParams = useMemo<string>(
    () => String(params?.textSearch || EMPTY_STRING),
    [params?.textSearch]
  );

  const currentRole = localStorage.getItem(StorageEnum.ROLE);
  const loadingContext = useContext(LoadingContext);
  //#endregion Destructuring Props

  //#region Declare Hook
  //#endregion Declare Hook

  //#region Selector
  const isRefreshTimeClockList = useSelector(selectIsRefreshTimeClockList);
  //#endregion Selector

  //#region Declare State
  const [paramObject, setParamObject] = useState<IQueryParamTimeClock>({
    page: pageSelected,
    limit: limitPage,
  });
  const [searchKey, setSearchKey] = useState<string>(EMPTY_STRING);
  const debounceSearchKey = useDebounce(searchKey, DEFAULT_DEBOUNCE_DELAY_TIME);

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [pagination, setPagination] = useState<IPaginationResponse>();
  const [totalItems, setTotalItems] = useState<number>();
  const [timeClockList, setTimeClockList] = useState<ITimeClock[]>([]);

  const [isShowConfirmDeleteTimeClock, setIsShowConfirmDeleteTimeClock] = useState<boolean>(false);
  const [timeClockSelected, setTimeClockSelected] = useState<ITimeClock | null>(null);

  const [filterParams, setFilterParams] = useState<IQueryParamTimeClock>(FILTER_DEFAULT_VALUES);

  const [employeeList, setEmployeeList] = useState<IBaseOption[]>([]);
  const [isLoadingEmployee, setIsLoadingEmployee] = useState<boolean>(false);
  const [searchKeyEmployee, setSearchKeyEmployee] = useState<string>(EMPTY_STRING);
  const debounceEmployeeSearchKey = useDebounce(searchKeyEmployee, DEFAULT_DEBOUNCE_DELAY_TIME);

  const [isShowExportPayroll, setIsShowExportPayroll] = useState<boolean>(false);
  //#endregion Declare State

  //#region Declare Memo
  //#endregion Declare Memo

  //#region Implement Hook
  useEffect(() => {
    handleGetTimeClockList(paramObject);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paramObject]);

  useEffect(() => {
    if (!isRefreshTimeClockList) return;

    handleGetTimeClockList(paramObject);
    dispatch(timeClockActions.setRefreshTimeClockList(false));

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isRefreshTimeClockList]);

  useEffect(() => {
    setSearchKey(textSearchParams);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [textSearchParams]);

  useEffect(() => {
    if (pagination || debounceSearchKey) {
      setParamObject({
        ...paramObject,
        page: BASE_PAGINATION_DEFAULT_CURRENT_PAGE,
        limit: limitPage,
        ...(debounceSearchKey ? { textSearch: debounceSearchKey } : { textSearch: undefined }),
      });

      setSearchParams({
        page: String(BASE_PAGINATION_DEFAULT_CURRENT_PAGE),
        limit: String(limitPage),
        ...(debounceSearchKey ? { textSearch: debounceSearchKey } : {}),
      });
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debounceSearchKey]);

  useEffect(() => {
    const params: IQueryParamBase = {
      page: BASE_PAGINATION_DEFAULT_CURRENT_PAGE,
      limit: DEFAULT_LIMIT_1000,
      status: StatusEnum.ACTIVE,
      ...(debounceEmployeeSearchKey && { textSearch: debounceEmployeeSearchKey }),
    };

    handleGetEmployeeList(params);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debounceEmployeeSearchKey]);
  //#endregion Implement Hook

  //#region Handle Function
  const handleGetEmployeeList = (params: IQueryParamBase) => {
    setIsLoadingEmployee(true);
    dispatch(getEmployeeList(params))
      .unwrap()
      .then((res) => {
        const { responses } = res?.data;
        if (!responses) return;

        const dataEmployee: IBaseOption[] = responses.map((item) => ({
          value: item.id,
          label: getFullName({ firstName: item.firstName, lastName: item.lastName }),
        }));

        setEmployeeList(dataEmployee);
      })
      .catch((error) => {})
      .finally(() => {
        setIsLoadingEmployee(false);
      });
  };

  const handleGetTimeClockList = (params: IQueryParamBase) => {
    setIsLoading(true);

    dispatch(getTimeClockList(params))
      .unwrap()
      .then((res) => {
        const data = res?.data;

        if (!data) return;

        data?.responses && setTimeClockList(data.responses);
        data?.totalItems && setTotalItems(data.totalItems);
        data?.pagination && setPagination(data.pagination);
      })
      .catch((error) => {})
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handleDelete = (timeClock: ITimeClock) => {
    if (!timeClock) return;

    setTimeClockSelected(timeClock);
    setIsShowConfirmDeleteTimeClock(true);
  };

  const handleView = (id: string) => {
    switch (currentRole) {
      case RoleEnum.ADMIN:
        navigate(`${adminRouteAbsolute.timeClock}/${id}`);
        break;

      case RoleEnum.STAFF:
        navigate(`${staffRouteAbsolute.timeClock}/${id}`);
        break;
    }
  };

  const handleRowTableClick = (id: string) => {
    switch (currentRole) {
      case RoleEnum.ADMIN:
        navigate(`${adminRouteAbsolute.timeClock}/${id}`);
        break;

      case RoleEnum.STAFF:
        navigate(`${staffRouteAbsolute.timeClock}/${id}`);
        break;
    }
  };

  const handleSearchChange = (value: string) => {
    setSearchKey(value);
  };

  const handlePageChange = (_event: ChangeEvent<unknown>, page: number) => {
    setParamObject({ ...paramObject, page });
    setSearchParams({ ...params, page: String(page), limit: String(limitPage) });
  };

  const handleRowPerPageChange = (rowPerPage: number) => {
    setParamObject({ ...paramObject, page: BASE_PAGINATION_DEFAULT_CURRENT_PAGE, limit: rowPerPage });
    setSearchParams({
      ...params,
      page: String(BASE_PAGINATION_DEFAULT_CURRENT_PAGE),
      limit: String(rowPerPage),
    });
  };

  const handleCloseConfirmDelete = () => {
    setIsShowConfirmDeleteTimeClock(false);
    setTimeClockSelected(null);
  };

  const handleConfirmDelete = () => {
    if (!timeClockSelected?.id) return;

    loadingContext?.show();

    dispatch(deleteTimeClock(timeClockSelected?.id))
      .unwrap()
      .then((res) => {
        dispatch(timeClockActions.setRefreshTimeClockList(true));
      })
      .catch((error) => {})
      .finally(() => {
        loadingContext?.hide();
        handleCloseConfirmDelete();
      });
  };

  const handleApplyFilter = (values: IQueryParamTimeClock | undefined) => {
    if (!values) {
      setFilterParams({});
      return;
    }

    setFilterParams({
      ...values,
    });

    setParamObject({
      ...paramObject,
      ...values,
    });
  };

  const handleSearchEmployeeChange = (searchKey: string) => {
    setSearchKeyEmployee(searchKey);
  };

  const handleFilterChange = (
    name: keyof IQueryParamTimeClock,
    value: string | string[],
    onChange?: (value: IFilterValueChange<IQueryParamTimeClock>) => void
  ) => {
    onChange?.({ name, value });
  };

  const handleChangeMonthExport = async (values: IExportPayroll) => {
    try {
      const { payrollObject } = values;
      if (!payrollObject) return;
      setIsLoading(true);

      const paramsExport: IParamsHoursWorked = {
        startTime: payrollObject?.startTime,
        endTime: payrollObject?.endTime,
      };
      const { data } = await dispatch(getTimeClockHoursWorked(paramsExport)).unwrap();

      const pdfDoc = (
        <PdfPayroll
          data={{
            month: formatTitleCase(payrollObject.label),
            data: data,
          }}
        />
      );
      const blob = await pdf(pdfDoc).toBlob();

      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = DEFAULT_PAYROLL_FILE_NAME_PDF;
      document.body.appendChild(link);
      link.click();

      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      setIsLoading(false);
    } catch (error) {
      setIsLoading(false);
    } finally {
      setIsLoading(false);
    }
  };

  const handleShowExportPayroll = () => {
    setIsShowExportPayroll(!isShowExportPayroll);
  };
  //#endregion Handle Function

  return (
    <PageShell title={t('menu_time_clock', { ns: 'common/sidebar' })}>
    <Box component={'div'} className={cx('container')} id='timeClockPage'>
      <BaseToolbar title={t('title_quantity_time_clock_page', { quantity: totalItems })}>
        <BaseInputSearch
          searchByContent={SearchByFilterContentEnum.JOB_NAME}
          value={searchKey}
          onChange={handleSearchChange}
        />

        <BaseFilters<IQueryParamTimeClock>
          value={filterParams}
          defaultValue={FILTER_DEFAULT_VALUES}
          onApply={handleApplyFilter}
        >
          {({ value, onChange }) => (
            <Stack gap={'12px'}>
              <Typography className={cx('title')}>
                {t('common_action_filter_by', { ns: 'common/base' })}
              </Typography>

              <FormGroup className={cx('filterBy')}>
                <Box component={'div'} className={cx('filterGroup')}>
                  <InputLabel className={cx('label')}>{t('filter_label_employees')}</InputLabel>

                  <BaseSelect
                    multiple
                    layout='horizontal'
                    width={'160px'}
                    options={employeeList || []}
                    value={value?.employeeIds}
                    onChange={(value: any) => handleFilterChange('employeeIds', value, onChange)}
                    isLoading={isLoadingEmployee}
                    onSearchChange={handleSearchEmployeeChange}
                  />
                </Box>
                <Box component={'div'} className={cx('filterGroup')}>
                  <InputLabel className={cx('label')}>{t('filter_label_date')}</InputLabel>

                  <BaseDatePicker
                    value={value?.date}
                    onChange={(value) => handleFilterChange('date', value, onChange)}
                    width={'160px'}
                  />
                </Box>
              </FormGroup>
            </Stack>
          )}
        </BaseFilters>

        {currentRole === RoleEnum.ADMIN && (
          <Button variant='contained' onClick={handleShowExportPayroll} className={cx('btn')}>
            <Typography className={cx('textBtn')}>{t('button_export_time_clock')}</Typography>
          </Button>
        )}
      </BaseToolbar>

      <Box component={'div'} className={cx('body')}>
        <BaseTable
          currentRowsPerPage={limitPage}
          currentPage={pageSelected}
          count={pagination?.totalPages || DEFAULT_NUMBER_ZERO}
          columns={columns(t, handleView, handleDelete)}
          dataSource={timeClockList || []}
          onPageChange={handlePageChange}
          onRowClick={handleRowTableClick}
          onRowsPerPageChange={handleRowPerPageChange}
        />
      </Box>

      {isShowConfirmDeleteTimeClock && (
        <ConfirmModal
          type='delete'
          icon={icons.tableIconActionDelete}
          title={t('common_title_confirm_delete', { ns: 'common/base' })}
          textConfirm={t('common_action_delete', { ns: 'common/base' })}
          onClose={handleCloseConfirmDelete}
          onConfirm={handleConfirmDelete}
        />
      )}

      {isShowExportPayroll && (
        <ChangeValueModal
          defaultValues={{
            payroll: EMPTY_STRING,
            payrollObject: {},
          }}
          title={t('modal_export_payroll_title')}
          textSubmit={t('common_action_export', {
            ns: 'common/base',
          })}
          onClose={handleShowExportPayroll}
          onSubmit={handleChangeMonthExport}
          schema={schema(t)}
          fields={[
            {
              name: 'payroll',
              label: t('modal_export_select_month_label'),
              options: dataMonthExport,
              required: true,
              objectFieldName: 'payrollObject',
            },
          ]}
        />
      )}

      {isLoading && <Loading />}
    </Box>
    </PageShell>
  );
};

export default TimeClock;
